/**
 * User Detail Page
 *
 * Displays user details with tabs for Profile, Permissions, and Activity.
 * Capability-gated to users with admin:users capability.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { Environment } from "@tatvex/types";
import {
  ArrowLeft,
  Save,
  Loader2,
  AlertCircle,
  RefreshCw,
  User,
  Shield,
  Activity,
  Check,
  Pause,
  Play,
  UserX,
  Mail,
  Calendar,
  Clock,
  Globe,
  Monitor,
  Trash2,
} from "lucide-react";
import type { ReactElement, FormEvent } from "react";
import { useState, useCallback, useEffect } from "react";
import { Link, Navigate, useParams, useNavigate } from "react-router-dom";

import { apiRequest } from "../../../api/client";
import { useAuthStore } from "../../../store/authStore";

import type {
  TenantUserView,
  UserDetailResponse,
  UserUpdateInput,
  UserDetailTab,
  UserConfirmationAction,
  SessionInfo,
  SessionListResponse,
} from "./types";
import {
  USER_STATUS_INFO,
  SYSTEM_ROLE_INFO,
  ENVIRONMENT_INFO,
  ALL_ENVIRONMENTS,
  SYSTEM_ROLES,
} from "./types";

export function UserDetailPage(): ReactElement {
  const { hasCapability } = useAuthStore();

  // Check capability
  if (!hasCapability("admin:users")) {
    return <Navigate to="/studio" replace />;
  }

  return <UserDetailContent />;
}

function UserDetailContent(): ReactElement {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<UserDetailTab>("profile");
  const [confirmAction, setConfirmAction] = useState<UserConfirmationAction | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["user", userId],
    queryFn: async () => {
      const response = await apiRequest<UserDetailResponse>(`/api/v1/admin/users/${userId}`);

      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    enabled: Boolean(userId),
  });

  const statusMutation = useMutation({
    mutationFn: async (action: "suspend" | "activate" | "remove") => {
      const endpoint =
        action === "remove"
          ? `/api/v1/admin/users/${userId}`
          : `/api/v1/admin/users/${userId}/${action}`;

      const response = await apiRequest<TenantUserView>(endpoint, {
        method: action === "remove" ? "DELETE" : "POST",
      });

      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    onSuccess: (_, action) => {
      if (action === "remove") {
        navigate("/studio/admin/users");
      } else {
        queryClient.invalidateQueries({ queryKey: ["user", userId] });
        queryClient.invalidateQueries({ queryKey: ["users"] });
      }
      setConfirmAction(null);
    },
  });

  const resendInviteMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest<{ success: boolean }>(
        `/api/v1/admin/users/${userId}/resend-invite`,
        { method: "POST" }
      );

      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    onSuccess: () => {
      setConfirmAction(null);
    },
  });

  const handleAction = useCallback((action: UserConfirmationAction): void => {
    setConfirmAction(action);
  }, []);

  const handleConfirmAction = useCallback((): void => {
    if (!confirmAction) return;

    if (confirmAction === "resendInvite") {
      resendInviteMutation.mutate();
    } else {
      statusMutation.mutate(
        confirmAction === "suspend"
          ? "suspend"
          : confirmAction === "activate"
            ? "activate"
            : "remove"
      );
    }
  }, [confirmAction, statusMutation, resendInviteMutation]);

  if (isLoading) {
    return <LoadingState />;
  }

  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : "Failed to load user"}
        onRetry={refetch}
      />
    );
  }

  if (!data) {
    return <Navigate to="/studio/admin/users" replace />;
  }

  const { user } = data;
  const statusInfo = USER_STATUS_INFO[user.status];

  return (
    <div className="p-6">
      {/* Page header */}
      <div className="mb-6">
        <Link
          to="/studio/admin/users"
          className="inline-flex items-center gap-1 text-sm text-foreground-muted hover:text-foreground mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Users
        </Link>

        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
              <User className="h-8 w-8" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold">{user.name}</h1>
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    statusInfo.color === "success"
                      ? "bg-success/10 text-success"
                      : statusInfo.color === "warning"
                        ? "bg-warning/10 text-warning"
                        : "bg-foreground-muted/10 text-foreground-muted"
                  }`}
                >
                  {statusInfo.label}
                </span>
              </div>
              <p className="mt-1 flex items-center gap-2 text-foreground-muted">
                <Mail className="h-4 w-4" />
                {user.email}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {user.status === "active" && (
              <button onClick={() => handleAction("suspend")} className="btn btn-secondary">
                <Pause className="mr-2 h-4 w-4" />
                Suspend
              </button>
            )}
            {user.status === "suspended" && (
              <button onClick={() => handleAction("activate")} className="btn btn-secondary">
                <Play className="mr-2 h-4 w-4" />
                Activate
              </button>
            )}
            <button
              onClick={() => handleAction("remove")}
              className="btn btn-secondary text-error hover:bg-error/10"
            >
              <UserX className="mr-2 h-4 w-4" />
              Remove
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-border">
        <nav className="-mb-px flex gap-4">
          <TabButton
            active={activeTab === "profile"}
            onClick={() => setActiveTab("profile")}
            icon={<User className="h-4 w-4" />}
            label="Profile"
          />
          <TabButton
            active={activeTab === "permissions"}
            onClick={() => setActiveTab("permissions")}
            icon={<Shield className="h-4 w-4" />}
            label="Permissions"
          />
          <TabButton
            active={activeTab === "activity"}
            onClick={() => setActiveTab("activity")}
            icon={<Activity className="h-4 w-4" />}
            label="Activity"
          />
          <TabButton
            active={activeTab === "sessions"}
            onClick={() => setActiveTab("sessions")}
            icon={<Monitor className="h-4 w-4" />}
            label="Sessions"
          />
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === "profile" && <ProfileTab user={user} />}
      {activeTab === "permissions" && <PermissionsTab user={user} userId={userId ?? ""} />}
      {activeTab === "activity" && <ActivityTab userId={userId ?? ""} />}
      {activeTab === "sessions" && <SessionsTab userId={userId ?? ""} />}

      {/* Confirmation Dialog */}
      {confirmAction && (
        <ConfirmationDialog
          action={confirmAction}
          userName={user.name}
          isLoading={statusMutation.isPending || resendInviteMutation.isPending}
          {...(statusMutation.isError
            ? {
                error:
                  statusMutation.error instanceof Error
                    ? statusMutation.error.message
                    : "An error occurred",
              }
            : {})}
          {...(resendInviteMutation.isError
            ? {
                error:
                  resendInviteMutation.error instanceof Error
                    ? resendInviteMutation.error.message
                    : "An error occurred",
              }
            : {})}
          onConfirm={handleConfirmAction}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </div>
  );
}

// ============================================================================
// Tab Button
// ============================================================================

interface TabButtonProps {
  readonly active: boolean;
  readonly onClick: () => void;
  readonly icon: ReactElement;
  readonly label: string;
}

function TabButton({ active, onClick, icon, label }: TabButtonProps): ReactElement {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium transition-colors ${
        active
          ? "border-primary text-primary"
          : "border-transparent text-foreground-muted hover:border-border hover:text-foreground"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

// ============================================================================
// Profile Tab
// ============================================================================

interface ProfileTabProps {
  readonly user: TenantUserView;
}

function ProfileTab({ user }: ProfileTabProps): ReactElement {
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {/* User Info */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">User Information</h2>
        <dl className="space-y-4">
          <div>
            <dt className="text-sm font-medium text-foreground-muted">Full Name</dt>
            <dd className="mt-1">{user.name}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-foreground-muted">Email Address</dt>
            <dd className="mt-1">{user.email}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-foreground-muted">Status</dt>
            <dd className="mt-1">
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                  USER_STATUS_INFO[user.status].color === "success"
                    ? "bg-success/10 text-success"
                    : USER_STATUS_INFO[user.status].color === "warning"
                      ? "bg-warning/10 text-warning"
                      : "bg-foreground-muted/10 text-foreground-muted"
                }`}
              >
                {USER_STATUS_INFO[user.status].label}
              </span>
            </dd>
          </div>
        </dl>
      </div>

      {/* Activity Summary */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Activity Summary</h2>
        <dl className="space-y-4">
          <div className="flex items-center gap-3">
            <Calendar className="h-5 w-5 text-foreground-muted" />
            <div>
              <dt className="text-sm font-medium text-foreground-muted">Added to Tenant</dt>
              <dd className="mt-0.5">{new Date(user.addedAt).toLocaleDateString()}</dd>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Clock className="h-5 w-5 text-foreground-muted" />
            <div>
              <dt className="text-sm font-medium text-foreground-muted">Last Login</dt>
              <dd className="mt-0.5">
                {user.lastLoginAt
                  ? new Date(user.lastLoginAt).toLocaleDateString()
                  : "Never logged in"}
              </dd>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Shield className="h-5 w-5 text-foreground-muted" />
            <div>
              <dt className="text-sm font-medium text-foreground-muted">Roles Assigned</dt>
              <dd className="mt-0.5">{user.roles.length}</dd>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Globe className="h-5 w-5 text-foreground-muted" />
            <div>
              <dt className="text-sm font-medium text-foreground-muted">Environment Access</dt>
              <dd className="mt-0.5">{user.environments.length} environments</dd>
            </div>
          </div>
        </dl>
      </div>
    </div>
  );
}

// ============================================================================
// Permissions Tab
// ============================================================================

interface PermissionsTabProps {
  readonly user: TenantUserView;
  readonly userId: string;
}

function PermissionsTab({ user, userId }: PermissionsTabProps): ReactElement {
  const queryClient = useQueryClient();
  const [selectedRoles, setSelectedRoles] = useState<string[]>([...user.roles]);
  const [selectedEnvironments, setSelectedEnvironments] = useState<Environment[]>([
    ...user.environments,
  ]);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const rolesChanged =
      selectedRoles.length !== user.roles.length ||
      selectedRoles.some((r) => !user.roles.includes(r));
    const envsChanged =
      selectedEnvironments.length !== user.environments.length ||
      selectedEnvironments.some((e) => !user.environments.includes(e));
    setHasChanges(rolesChanged || envsChanged);
  }, [selectedRoles, selectedEnvironments, user.roles, user.environments]);

  const updateMutation = useMutation({
    mutationFn: async (input: UserUpdateInput) => {
      const response = await apiRequest<TenantUserView>(`/api/v1/admin/users/${userId}`, {
        method: "PATCH",
        body: input,
      });

      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user", userId] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
      setHasChanges(false);
    },
  });

  const handleRoleToggle = useCallback((roleId: string): void => {
    setSelectedRoles((prev) =>
      prev.includes(roleId) ? prev.filter((r) => r !== roleId) : [...prev, roleId]
    );
  }, []);

  const handleEnvironmentToggle = useCallback((env: Environment): void => {
    setSelectedEnvironments((prev) =>
      prev.includes(env) ? prev.filter((e) => e !== env) : [...prev, env]
    );
  }, []);

  const handleSave = useCallback(
    (e: FormEvent): void => {
      e.preventDefault();
      updateMutation.mutate({
        roles: selectedRoles,
        environments: selectedEnvironments,
      });
    },
    [selectedRoles, selectedEnvironments, updateMutation]
  );

  return (
    <form onSubmit={handleSave} className="space-y-6">
      {/* Roles */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-2">Roles</h2>
        <p className="text-sm text-foreground-muted mb-4">
          Select the roles this user should have. Roles determine what actions the user can perform.
        </p>
        <div className="space-y-3">
          {SYSTEM_ROLES.map((roleId) => {
            const roleInfo = SYSTEM_ROLE_INFO[roleId];
            const isSelected = selectedRoles.includes(roleId);

            return (
              <label
                key={roleId}
                className={`flex items-start gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                  isSelected
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleRoleToggle(roleId)}
                  className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-primary"
                />
                <div className="flex-1">
                  <p className="font-medium">{roleInfo?.label ?? roleId}</p>
                  <p className="text-sm text-foreground-muted">
                    {roleInfo?.description ?? "Custom role"}
                  </p>
                </div>
                {isSelected && <Check className="h-5 w-5 text-primary" />}
              </label>
            );
          })}
        </div>
      </div>

      {/* Environments */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-2">Environment Access</h2>
        <p className="text-sm text-foreground-muted mb-4">
          Select which environments this user can access. Production access should be limited.
        </p>
        <div className="grid gap-3 sm:grid-cols-3">
          {ALL_ENVIRONMENTS.map((env) => {
            const envInfo = ENVIRONMENT_INFO[env];
            const isSelected = selectedEnvironments.includes(env);

            return (
              <label
                key={env}
                className={`flex items-center gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                  isSelected
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50"
                }`}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleEnvironmentToggle(env)}
                  className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                />
                <div className="flex-1">
                  <p className="font-medium">{envInfo.label}</p>
                  <p className="text-xs text-foreground-muted">{envInfo.description}</p>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      {/* Error message */}
      {updateMutation.isError && (
        <div className="rounded-md border border-error bg-error/10 p-4">
          <p className="text-sm text-error">
            {updateMutation.error instanceof Error
              ? updateMutation.error.message
              : "Failed to update permissions"}
          </p>
        </div>
      )}

      {/* Save button */}
      {hasChanges && (
        <div className="flex justify-end">
          <button type="submit" disabled={updateMutation.isPending} className="btn btn-primary">
            {updateMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save Changes
              </>
            )}
          </button>
        </div>
      )}
    </form>
  );
}

// ============================================================================
// Activity Tab
// ============================================================================

interface ActivityTabProps {
  readonly userId: string;
}

interface ActivityLogEntry {
  readonly id: string;
  readonly action: string;
  readonly details: string;
  readonly timestamp: string;
  readonly ipAddress?: string;
}

function ActivityTab({ userId }: ActivityTabProps): ReactElement {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["user-activity", userId],
    queryFn: async () => {
      const response = await apiRequest<{ items: readonly ActivityLogEntry[] }>(
        `/api/v1/admin/users/${userId}/activity`
      );

      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-foreground-muted" />
      </div>
    );
  }

  if (isError || !data?.items.length) {
    return (
      <div className="card">
        <div className="flex flex-col items-center justify-center py-8">
          <Activity className="h-8 w-8 text-foreground-muted" />
          <p className="mt-4 text-foreground-muted">No activity recorded yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <h2 className="text-lg font-semibold mb-4">Recent Activity</h2>
      <div className="space-y-4">
        {data.items.map((entry) => (
          <div key={entry.id} className="flex gap-4 border-b border-border pb-4 last:border-0">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-background-secondary">
              <Activity className="h-4 w-4 text-foreground-muted" />
            </div>
            <div className="flex-1">
              <p className="font-medium">{entry.action}</p>
              <p className="text-sm text-foreground-muted">{entry.details}</p>
              <p className="mt-1 text-xs text-foreground-muted">
                {new Date(entry.timestamp).toLocaleString()}
                {entry.ipAddress && ` • ${entry.ipAddress}`}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Sessions Tab
// ============================================================================

interface SessionsTabProps {
  readonly userId: string;
}

/**
 * Parse a raw user-agent string into a short readable device label.
 * Returns the raw value truncated to 60 chars when no known token is found.
 */
function parseUserAgent(userAgent: string | null): string {
  if (!userAgent) return "Unknown device";

  if (/iPhone/.test(userAgent)) return "iPhone";
  if (/iPad/.test(userAgent)) return "iPad";
  if (/Android/.test(userAgent)) return "Android";

  const browserMatch = userAgent.match(/(Chrome|Firefox|Safari|Edge|OPR|Opera)\/[\d.]+/);
  const browser = browserMatch ? browserMatch[0] : null;

  const osMatch = userAgent.match(/\(([^)]+)\)/);
  const os = osMatch ? osMatch[1]?.split(";")[0]?.trim() : null;

  if (browser && os) return `${browser} on ${os}`;
  if (browser) return browser;

  return userAgent.length > 60 ? `${userAgent.slice(0, 60)}…` : userAgent;
}

function SessionsTab({ userId }: SessionsTabProps): ReactElement {
  const queryClient = useQueryClient();
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [revokeAllPending, setRevokeAllPending] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["user-sessions", userId],
    queryFn: async () => {
      const response = await apiRequest<SessionListResponse>(
        `/api/v1/admin/users/${userId}/sessions`
      );

      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
  });

  const revokeOneMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      const response = await apiRequest<{ success: boolean }>(
        `/api/v1/admin/users/${userId}/sessions/${sessionId}`,
        { method: "DELETE" }
      );

      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    onSuccess: () => {
      setRevokingId(null);
      queryClient.invalidateQueries({ queryKey: ["user-sessions", userId] });
    },
    onError: () => {
      setRevokingId(null);
    },
  });

  const revokeAllMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest<{ success: boolean; revokedCount: number }>(
        `/api/v1/admin/users/${userId}/revoke-sessions`,
        { method: "POST" }
      );

      if (!response.success) {
        throw new Error(response.error.message);
      }
      return response.data;
    },
    onSuccess: () => {
      setRevokeAllPending(false);
      queryClient.invalidateQueries({ queryKey: ["user-sessions", userId] });
    },
    onError: () => {
      setRevokeAllPending(false);
    },
  });

  const handleRevokeOne = useCallback(
    (session: SessionInfo): void => {
      setRevokingId(session.id);
      revokeOneMutation.mutate(session.id);
    },
    [revokeOneMutation]
  );

  const handleRevokeAll = useCallback((): void => {
    setRevokeAllPending(true);
    revokeAllMutation.mutate();
  }, [revokeAllMutation]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-foreground-muted" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="card">
        <div className="flex flex-col items-center justify-center py-8">
          <AlertCircle className="h-8 w-8 text-error" />
          <p className="mt-4 text-foreground-muted">Failed to load sessions</p>
          <button onClick={() => refetch()} className="btn btn-secondary mt-4">
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  const sessions: readonly SessionInfo[] = data?.data ?? [];

  if (sessions.length === 0) {
    return (
      <div className="card">
        <div className="flex flex-col items-center justify-center py-8">
          <Monitor className="h-8 w-8 text-foreground-muted" />
          <p className="mt-4 text-foreground-muted">No active sessions</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Revoke All */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Active Sessions</h2>
        <button
          onClick={handleRevokeAll}
          disabled={revokeAllMutation.isPending || revokeAllPending}
          className="btn btn-secondary text-error hover:bg-error/10"
        >
          {revokeAllMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Revoking All...
            </>
          ) : (
            <>
              <Trash2 className="mr-2 h-4 w-4" />
              Revoke All Sessions
            </>
          )}
        </button>
      </div>

      {/* Error banner */}
      {(revokeOneMutation.isError || revokeAllMutation.isError) && (
        <div className="rounded-md border border-error bg-error/10 p-3">
          <p className="text-sm text-error">
            {revokeOneMutation.isError
              ? revokeOneMutation.error instanceof Error
                ? revokeOneMutation.error.message
                : "Failed to revoke session"
              : revokeAllMutation.error instanceof Error
                ? revokeAllMutation.error.message
                : "Failed to revoke all sessions"}
          </p>
        </div>
      )}

      {/* Session table */}
      <div className="card overflow-hidden p-0">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-background-secondary">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-foreground-muted">Device</th>
              <th className="px-4 py-3 text-left font-medium text-foreground-muted">IP Address</th>
              <th className="px-4 py-3 text-left font-medium text-foreground-muted">Created</th>
              <th className="px-4 py-3 text-left font-medium text-foreground-muted">Last Active</th>
              <th className="px-4 py-3 text-left font-medium text-foreground-muted">Expires</th>
              <th className="px-4 py-3 text-right font-medium text-foreground-muted">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sessions.map((session) => (
              <tr key={session.id} className="hover:bg-background-secondary/50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Monitor className="h-4 w-4 shrink-0 text-foreground-muted" />
                    <span className="max-w-[200px] truncate" title={session.userAgent ?? undefined}>
                      {parseUserAgent(session.userAgent)}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 font-mono text-xs">{session.ipAddress ?? "—"}</td>
                <td className="px-4 py-3 text-foreground-muted">
                  {new Date(session.createdAt).toLocaleDateString()}
                </td>
                <td className="px-4 py-3 text-foreground-muted">
                  {session.lastUsedAt ? new Date(session.lastUsedAt).toLocaleString() : "—"}
                </td>
                <td className="px-4 py-3 text-foreground-muted">
                  {new Date(session.expiresAt).toLocaleDateString()}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => handleRevokeOne(session)}
                    disabled={revokeOneMutation.isPending && revokingId === session.id}
                    className="btn btn-secondary text-xs text-error hover:bg-error/10"
                  >
                    {revokeOneMutation.isPending && revokingId === session.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      "Revoke"
                    )}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// Confirmation Dialog
// ============================================================================

interface ConfirmationDialogProps {
  readonly action: UserConfirmationAction;
  readonly userName: string;
  readonly isLoading: boolean;
  readonly error?: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

function ConfirmationDialog({
  action,
  userName,
  isLoading,
  error,
  onConfirm,
  onCancel,
}: ConfirmationDialogProps): ReactElement {
  const actionConfig: Record<
    UserConfirmationAction,
    {
      title: string;
      description: string;
      confirmLabel: string;
      variant: "warning" | "error" | "primary";
    }
  > = {
    suspend: {
      title: "Suspend User",
      description: `Are you sure you want to suspend ${userName}? They will not be able to access any tenant resources until reactivated.`,
      confirmLabel: "Suspend User",
      variant: "warning",
    },
    activate: {
      title: "Activate User",
      description: `Are you sure you want to activate ${userName}? They will regain access to tenant resources based on their assigned roles.`,
      confirmLabel: "Activate User",
      variant: "primary",
    },
    remove: {
      title: "Remove User",
      description: `Are you sure you want to remove ${userName} from this tenant? This action cannot be undone. The user will lose all access to this tenant.`,
      confirmLabel: "Remove User",
      variant: "error",
    },
    resendInvite: {
      title: "Resend Invitation",
      description: `Are you sure you want to resend the invitation email to ${userName}?`,
      confirmLabel: "Resend Invitation",
      variant: "primary",
    },
  };

  const config = actionConfig[action];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />

      {/* Dialog */}
      <div className="relative w-full max-w-md rounded-lg bg-background p-6 shadow-xl">
        <h2 className="text-lg font-semibold">{config.title}</h2>
        <p className="mt-2 text-sm text-foreground-muted">{config.description}</p>

        {error && (
          <div className="mt-4 rounded-md border border-error bg-error/10 p-3">
            <p className="text-sm text-error">{error}</p>
          </div>
        )}

        <div className="mt-6 flex items-center justify-end gap-3">
          <button onClick={onCancel} disabled={isLoading} className="btn btn-secondary">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`btn ${
              config.variant === "error"
                ? "bg-error text-white hover:bg-error/90"
                : config.variant === "warning"
                  ? "bg-warning text-white hover:bg-warning/90"
                  : "btn-primary"
            }`}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              config.confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// State Components
// ============================================================================

function LoadingState(): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-foreground-muted">
      <Loader2 className="h-8 w-8 animate-spin" />
      <p className="mt-4">Loading user...</p>
    </div>
  );
}

interface ErrorStateProps {
  readonly message: string;
  readonly onRetry: () => void;
}

function ErrorState({ message, onRetry }: ErrorStateProps): ReactElement {
  return (
    <div className="p-6">
      <Link
        to="/studio/admin/users"
        className="inline-flex items-center gap-1 text-sm text-foreground-muted hover:text-foreground mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Users
      </Link>
      <div className="flex flex-col items-center justify-center py-12">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-error/10">
          <AlertCircle className="h-6 w-6 text-error" />
        </div>
        <p className="mt-4 text-foreground-muted">{message}</p>
        <button onClick={onRetry} className="btn btn-secondary mt-4">
          <RefreshCw className="mr-2 h-4 w-4" />
          Try again
        </button>
      </div>
    </div>
  );
}
